import React, { useState } from 'react';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../config/firebase';

export default function PhotoUpload({ onPhotosUpdate, existingPhotos = [], storyId, questionId, answerId, storagePrefix = 'photos' }) {
  const [photos, setPhotos] = useState(existingPhotos);
  const [uploading, setUploading] = useState(false);

  // Image compression utility function
  const compressImage = (file, maxWidth = 1200, quality = 0.8) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        // Calculate new dimensions while maintaining aspect ratio
        let { width, height } = img;
        
        if (width > height) {
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
        } else {
          if (height > maxWidth) {
            width = (width * maxWidth) / height;
            height = maxWidth;
          }
        }
        
        // Set canvas dimensions and draw image
        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);
        
        // Convert to blob with compression
        canvas.toBlob(
          (blob) => {
            // Create a new file with compressed data
            const compressedFile = new File([blob], file.name, {
              type: 'image/jpeg',
              lastModified: Date.now(),
            });
            resolve(compressedFile);
          },
          'image/jpeg',
          quality
        );
      };
      
      img.src = URL.createObjectURL(file);
    });
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    try {
      const newPhotos = [];
      
      for (const file of files) {
        // Validate file type
        if (!file.type.startsWith('image/')) {
          alert(`${file.name} is not an image file`);
          continue;
        }

        // Original file size validation (now more lenient since we'll compress)
        if (file.size > 100 * 1024 * 1024) { // 100MB limit for original (handles iPhone ProRAW)
          alert(`${file.name} is too large. Please choose images under 100MB.`);
          continue;
        }

        // Compress the image before upload
        const originalSize = (file.size / 1024 / 1024).toFixed(2);
        console.log(`Original image size: ${originalSize}MB`);
        
        const compressedFile = await compressImage(file);
        const compressedSize = (compressedFile.size / 1024 / 1024).toFixed(2);
        console.log(`Compressed image size: ${compressedSize}MB (${Math.round((1 - compressedFile.size / file.size) * 100)}% reduction)`);

        // Generate unique photo ID and proper storage path
        const photoId = `photo_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        
        // Create proper hierarchical storage path following the question/answer structure
        let storagePath;
        if (storyId && questionId && answerId) {
          // Photo for a specific answer under a question - proper hierarchy
          storagePath = `stories/${storyId}/questions/${questionId}/answers/${answerId}/photos/${photoId}`;
        } else if (storyId && questionId) {
          // Photo for a question (fallback if no answer ID)
          storagePath = `stories/${storyId}/questions/${questionId}/photos/${photoId}`;
        } else if (storyId) {
          // Photo for the story itself (cover photo)
          storagePath = `stories/${storyId}/photos/${photoId}`;
        } else {
          // Fallback to generic path
          storagePath = `${storagePrefix}/${photoId}`;
        }
        
        const storageRef = ref(storage, storagePath);
        
        // Upload the compressed file instead of original
        await uploadBytes(storageRef, compressedFile);
        const downloadURL = await getDownloadURL(storageRef);
        
        newPhotos.push({
          id: photoId,
          url: downloadURL,
          fileName: file.name,
          caption: '', // Initialize with empty caption - user can add later
          uploadedAt: new Date().toISOString(),
          storagePath,
          originalSize: file.size,
          compressedSize: compressedFile.size,
          compressionRatio: Math.round((1 - compressedFile.size / file.size) * 100)
        });
      }

      const updatedPhotos = [...photos, ...newPhotos];
      setPhotos(updatedPhotos);
      onPhotosUpdate(updatedPhotos);
      
    } catch (error) {
      console.error('Error uploading photos:', error);
      alert('Failed to upload photos. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleRemovePhoto = (photoId) => {
    const updatedPhotos = photos.filter(photo => photo.id !== photoId);
    setPhotos(updatedPhotos);
    onPhotosUpdate(updatedPhotos);
  };

  const handleCaptionChange = (photoId, newCaption) => {
    const updatedPhotos = photos.map(photo => 
      photo.id === photoId ? { ...photo, caption: newCaption } : photo
    );
    setPhotos(updatedPhotos);
    onPhotosUpdate(updatedPhotos);
  };

  return (
    <div className="photo-upload">
      <label 
        htmlFor="photo-upload-input"
        className="upload-label"
        style={{
          display: 'block',
          textAlign: 'center',
          marginBottom: '1rem',
          cursor: 'pointer',
          padding: '1.5rem',
          border: '2px dashed #C87400',
          borderRadius: '8px',
          backgroundColor: '#FFF9E6',
          transition: 'background-color 0.2s ease'
        }}
        onMouseEnter={(e) => e.target.style.backgroundColor = '#FFF3D3'}
        onMouseLeave={(e) => e.target.style.backgroundColor = '#FFF9E6'}
      >
        <div style={{ fontSize: '1.2rem', marginBottom: '0.5rem' }}>
          📷 Add Photos (optional)
        </div>
        <div style={{ fontSize: '0.8rem', color: '#888', marginTop: '0.25rem' }}>
          Maximum 20MB per image • JPEG, PNG, WebP supported
        </div>
      </label>
      
      <input
        id="photo-upload-input"
        type="file"
        accept="image/*"
        multiple
        onChange={handlePhotoUpload}
        disabled={uploading}
        style={{ display: 'none' }}
      />
      
      {uploading && (
        <div style={{ 
          textAlign: 'center', 
          color: '#C87400',
          marginTop: '1rem',
          padding: '1rem',
          backgroundColor: '#FFF9E6',
          borderRadius: '8px',
          border: '1px solid #C87400'
        }}>
          <div>📤 Compressing and uploading photos...</div>
          <div style={{ fontSize: '0.9rem', marginTop: '0.5rem', color: '#666' }}>
            Optimizing images for faster loading and storage efficiency
          </div>
        </div>
      )}

      {photos.length > 0 && (
        <div style={{ marginTop: '1rem' }}>
          <h4 style={{ color: '#061D3D', marginBottom: '0.5rem' }}>
            Uploaded Photos ({photos.length})
          </h4>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
            gap: '1rem'
          }}>
            {photos.map(photo => (
              <div key={photo.id} style={{ 
                position: 'relative',
                border: '1px solid #C87400',
                borderRadius: '8px',
                overflow: 'hidden',
                backgroundColor: '#FFF'
              }}>
                <img
                  src={photo.url}
                  alt={photo.caption || photo.fileName}
                  style={{
                    width: '100%',
                    height: '120px',
                    objectFit: 'cover'
                  }}
                />
                <div style={{
                  padding: '0.5rem',
                  fontSize: '0.8rem',
                  color: '#061D3D'
                }}>
                  <div style={{ fontWeight: '500', marginBottom: '0.25rem' }}>
                    {photo.fileName}
                  </div>
                  {photo.compressedSize && (
                    <div style={{ 
                      color: '#666', 
                      fontSize: '0.7rem',
                      marginTop: '0.25rem'
                    }}>
                      {(photo.compressedSize / 1024 / 1024).toFixed(1)}MB
                    </div>
                  )}
                  
                  {/* Show current caption if exists */}
                  {photo.caption && photo.caption.trim() && (
                    <div style={{ 
                      marginTop: '0.5rem',
                      padding: '0.5rem',
                      backgroundColor: '#FFF9E6',
                      borderRadius: '4px',
                      border: '1px solid #C87400',
                      fontSize: '0.75rem',
                      fontStyle: 'italic',
                      color: '#333',
                      lineHeight: '1.4'
                    }}>
                      "{photo.caption}"
                    </div>
                  )}
                  
                  {/* Caption input field */}
                  <div style={{ marginTop: '0.5rem' }}>
                    <input
                      type="text"
                      placeholder="Add caption (optional)..."
                      value={photo.caption || ''}
                      onChange={(e) => handleCaptionChange(photo.id, e.target.value)}
                      style={{
                        width: '100%',
                        padding: '0.4rem',
                        fontSize: '0.75rem',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        outline: 'none',
                        transition: 'border-color 0.2s'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#C87400'}
                      onBlur={(e) => e.target.style.borderColor = '#ddd'}
                    />
                    <div style={{ 
                      fontSize: '0.65rem', 
                      color: '#666',
                      marginTop: '0.25rem'
                    }}>
                      {photo.caption && photo.caption.trim() 
                        ? '✓ Caption will appear in PDF memoir'
                        : 'Captions appear in italic below photos in PDF'
                      }
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => handleRemovePhoto(photo.id)}
                  style={{
                    position: 'absolute',
                    top: '5px',
                    right: '5px',
                    background: 'rgba(255, 255, 255, 0.9)',
                    border: 'none',
                    borderRadius: '50%',
                    width: '24px',
                    height: '24px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '14px',
                    color: '#dc2626'
                  }}
                  title="Remove photo"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
